import React from "react";
import { Quote, ExternalLink, Linkedin } from "lucide-react";
import { Button } from "./ui/button";

const LINKEDIN_URL = "https://www.linkedin.com/in/vjrajgopalan/";

// Verbatim LinkedIn recommendations (from vjrajgopalan's profile)
const QUOTES = [
  {
    body:
      "Vijay is a very experienced PeopleSoft Technical resource with a good functional background across several HR and FSCM modules. In his role as the PeopleSoft IT Team Lead he was instrumental in the success of the EKPC upgrade. Fellow consultants and clients alike love Vijay! I would welcome the opportunity to work with such a talented and dedicated consultant anytime.",
    name: "Linda Son Hing",
    role: "PeopleSoft Engagement Lead",
    feature: true,
  },
  {
    body:
      "Vijay is one of the best PeopleSoft technical leads I've had the pleasure to work with. His knowledge of PeopleSoft is excellent and his ability to do research and find the solution is a great skill. His communication skills are outstanding and he is a great team player.",
    name: "Karin Morgan, PMP, MBA",
    role: "Project Manager",
    feature: true,
  },
  {
    body:
      "Vijay is one of the best technical resources that I have worked with. He is very eager to get the job done, and is willing to take the time to debug issues so that the entire process is working correctly. He is very thorough in his work, which results in no rework. He is a highly respected consultant by his peers and his clients. I welcome the opportunity to work with Vijay in the future, and I highly recommend him to any client.",
    name: "Fateh Shams",
    role: "PeopleSoft Practice Lead",
    feature: true,
  },
  {
    body:
      "Vijay has terrific PeopleSoft knowledge and was always proactive in resolving complex issues with quick turnaround time while deploying code in various environments. He was a great asset to the PeopleSoft upgrade team and was instrumental in deploying the application within scheduled time frames.",
    name: "Vijay K Datla",
    role: "PeopleSoft Upgrade Team",
  },
  {
    body:
      "We recently converted our payroll system over to PeopleSoft Payroll/HRMS 9.0. During that time I had the pleasure of working with Vijay, who played a key role in the success of the project. Not only was Vijay's technical knowledge of PeopleSoft excellent, he used that knowledge to come up with innovative solutions to meet the needs of the functional users. He was a dedicated team member and always maintained a positive attitude.",
    name: "Charles Rico",
    role: "Payroll / HRMS Implementation",
  },
  {
    body:
      "It was a great opportunity to have Vijay as a core member in my team. He ensured that we meet the deadline through multiple stages of software implementation. He is extremely hardworking, energetic and dedicated. Vijay believes in completing the work chain. He keeps himself upgraded all the time and ensures his team members have the same understanding.",
    name: "Shubhabrata Sengupta",
    role: "Delivery Lead",
  },
  {
    body:
      "Vijay was an excellent addition to our team — he not only served as our PeopleSoft technology expert, he also fit into the team and helped in many other ways. Had we been in a better position to do so, I really wanted to try to hire him on full time. The value and results he was able to achieve greatly added to the success of the project.",
    name: "LinkedIn Member",
    role: "Client Sponsor",
  },
];

const Card = ({ q, large = false }) => (
  <figure
    className={`bg-[#1F2326] p-8 md:p-10 flex flex-col gap-6 ${large ? "md:p-12" : ""}`}
    data-testid={`recommendation-${q.name.replace(/\s+/g, "-").toLowerCase()}`}
  >
    <Quote className={`text-[#901A1E] ${large ? "w-10 h-10" : "w-8 h-8"}`} strokeWidth={1.2} />
    <blockquote
      className={`font-serif text-[#F7F5F0] leading-snug ${
        large ? "text-xl md:text-2xl" : "text-base md:text-lg"
      }`}
      style={{ fontWeight: 400 }}
    >
      &ldquo;{q.body}&rdquo;
    </blockquote>
    <figcaption className="mt-auto pt-6 border-t border-[#F7F5F0]/15">
      <div className="font-serif text-[#F7F5F0] text-base" style={{ fontWeight: 600 }}>
        {q.name}
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E89A9C] mt-2">
        {q.role}
      </div>
    </figcaption>
  </figure>
);

export const Recommendations = () => {
  const featured = QUOTES.filter((q) => q.feature);
  const rest = QUOTES.filter((q) => !q.feature);

  return (
    <section
      id="recommendations"
      className="relative py-24 md:py-32 bg-[#1F2326] text-[#F7F5F0] overflow-hidden"
      data-testid="recommendations-section"
    >
      <div aria-hidden="true" className="absolute inset-0 grain opacity-40" />
      <div className="container-app relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#F7F5F0]/60" />
              <span className="label-eyebrow text-[#E89A9C]">04 · Recommendations</span>
            </div>
            <h2
              className="font-serif text-balance"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.25rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
                fontWeight: 600,
              }}
              data-testid="recommendations-headline"
            >
              In the words of the people
              <br />
              he&rsquo;s worked alongside.
            </h2>
          </div>
          <div className="lg:col-span-5 self-end">
            <p className="text-[#F7F5F0]/70 leading-relaxed" data-testid="recommendations-intro">
              Recommendations from project managers, practice leads and clients
              across two decades of PeopleSoft engagements — reproduced verbatim
              from LinkedIn.
            </p>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="linkedin-recommendations-link"
            >
              <Button
                variant="outline"
                className="mt-6 rounded-none border-[#F7F5F0]/40 bg-transparent text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1F2326] h-auto px-6 py-3 text-xs tracking-[0.2em] uppercase"
              >
                <Linkedin className="w-4 h-4 mr-2" />
                View All on LinkedIn
                <ExternalLink className="w-3.5 h-3.5 ml-2" />
              </Button>
            </a>
          </div>
        </div>

        {/* Featured row — 3 large quotes */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#F7F5F0]/10 mb-px"
          data-testid="recommendations-featured"
        >
          {featured.map((q, i) => (
            <Card key={i} q={q} large />
          ))}
        </div>

        {/* Remaining quotes — compact grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F7F5F0]/10"
          data-testid="recommendations-more"
        >
          {rest.map((q, i) => (
            <Card key={i} q={q} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Recommendations;
